/**
 * CatalogUpload — GVW-122 Dataset onboarding (TUS upload + validate)
 * 
 * Per brief.md: Workspace shell with center = upload region: large Dropzone + file list + 
 * validation report + progress bar. Right panel: "Validation findings" tab.
 */

import React, { useState, useCallback } from 'react';
import { catalogClient, type CatalogResult } from '../../catalog/catalogClient';

interface CatalogUploadProps {
  className?: string;
}

interface FileUpload {
  id: string;
  file: File;
  status: 'waiting' | 'validating' | 'ready' | 'needs-input' | 'failed' | 'completed';
  progress: number;
  error?: string;
  jobId?: string;
}

interface CatalogUploadState {
  files: FileUpload[];
  dragActive: boolean;
  uploading: boolean;
  backendError: string | null;
}

export function CatalogUpload({ className }: CatalogUploadProps): JSX.Element {
  const [state, setState] = useState<CatalogUploadState>({
    files: [],
    dragActive: false,
    uploading: false,
    backendError: null,
  });

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setState(prev => ({ ...prev, dragActive: true }));
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setState(prev => ({ ...prev, dragActive: false }));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setState(prev => ({ ...prev, dragActive: false }));

    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      addFiles(selectedFiles);
    }
  }, []);

  const addFiles = (fileList: File[]) => {
    const newFiles: FileUpload[] = fileList.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      status: 'waiting',
      progress: 0,
    }));

    setState(prev => ({
      ...prev,
      files: [...prev.files, ...newFiles],
    }));
  };

  const startUpload = async (fileUpload: FileUpload) => {
    setState(prev => ({
      ...prev,
      files: prev.files.map(f => 
        f.id === fileUpload.id 
          ? { ...f, status: 'validating', progress: 0 }
          : f
      ),
      backendError: null,
    }));

    const result: CatalogResult<{ uploadUrl: string; jobId: string }> = await catalogClient.createUpload(fileUpload.file);

    if (result.ok) {
      // In a real implementation, we would use the TUS client to upload to result.data.uploadUrl
      // For now, we'll simulate the upload process
      setState(prev => ({
        ...prev,
        files: prev.files.map(f => 
          f.id === fileUpload.id 
            ? { ...f, status: 'completed', progress: 100, jobId: result.data.jobId }
            : f
        ),
      }));
    } else {
      let errorMessage = 'Upload failed';
      if (result.reason === 'backend-not-deployed') {
        errorMessage = 'Backend not deployed. Upload service is not available.';
        setState(prev => ({ ...prev, backendError: errorMessage }));
      } else if (result.reason === 'unauthorized') {
        errorMessage = 'Upload requires editor role.';
      }
      
      setState(prev => ({
        ...prev,
        files: prev.files.map(f => 
          f.id === fileUpload.id 
            ? { ...f, status: 'failed', error: errorMessage }
            : f
        ),
      }));
    }
  };

  const removeFile = (fileId: string) => {
    setState(prev => ({
      ...prev,
      files: prev.files.filter(f => f.id !== fileId),
    }));
  };

  const getStatusIcon = (status: FileUpload['status']) => {
    switch (status) {
      case 'waiting': return 'schedule';
      case 'validating': return 'hourglass_empty';
      case 'ready': return 'check_circle';
      case 'needs-input': return 'info';
      case 'failed': return 'error';
      case 'completed': return 'check';
      default: return 'help';
    }
  };

  const getStatusColor = (status: FileUpload['status']) => {
    switch (status) {
      case 'waiting': return 'neutral';
      case 'validating': return 'info';
      case 'ready':
      case 'completed': return 'success';
      case 'needs-input': return 'warning';
      case 'failed': return 'error';
      default: return 'neutral';
    }
  };

  return (
    <div className={`catalog-upload ${className || ''}`} data-testid="catalog-upload">
      <header className="catalog-upload__header">
        <h1>
          <span className="material-symbols-outlined" aria-hidden="true">upload_file</span>
          Upload Dataset
        </h1>
      </header>

      {state.backendError && (
        <div className="catalog-upload__banner catalog-upload__banner--error" role="alert">
          <span className="material-symbols-outlined" aria-hidden="true">error</span>
          <p>{state.backendError}</p>
        </div>
      )}

      <div className="catalog-upload__content">
        {/* Main upload area */}
        <main className="catalog-upload__main">
          {/* Dropzone */}
          <div
            className={`catalog-upload__dropzone ${
              state.dragActive ? 'catalog-upload__dropzone--active' : ''
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            role="region"
            aria-label="Drop zone, or browse files"
          >
            <span className="material-symbols-outlined catalog-upload__dropzone-icon" aria-hidden="true">
              {state.dragActive ? 'cloud_upload' : 'upload_file'}
            </span>
            
            <h2>
              {state.dragActive ? 'Drop to upload' : 'Drop files below to upload'}
            </h2>
            
            <p>or</p>
            
            <label className="catalog-upload__browse-button">
              <input
                type="file"
                multiple
                onChange={handleFileInput}
                className="catalog-upload__file-input"
                aria-label="Browse files to upload"
              />
              Browse files
            </label>
            
            <p className="catalog-upload__helper">
              Supported formats: GeoTIFF, Shapefile, GeoJSON, KML, LAS, LAZ
            </p>
          </div>

          {/* File list */}
          {state.files.length > 0 && (
            <div className="catalog-upload__files">
              <h2>Files</h2>
              
              <table className="catalog-upload__files-table">
                <caption className="catalog-upload__table-caption">
                  Upload progress for {state.files.length} file(s)
                </caption>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Size</th>
                    <th>Status</th>
                    <th>Progress</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {state.files.map((fileUpload) => (
                    <tr key={fileUpload.id}>
                      <td>{fileUpload.file.name}</td>
                      <td>{(fileUpload.file.size / (1024 * 1024)).toFixed(2)} MB</td>
                      <td>
                        <span className={`catalog-upload__status-chip catalog-upload__status-chip--${getStatusColor(fileUpload.status)}`}>
                          <span className="material-symbols-outlined" aria-hidden="true">
                            {getStatusIcon(fileUpload.status)}
                          </span>
                          {fileUpload.status}
                        </span>
                      </td>
                      <td>
                        {fileUpload.status === 'validating' ? (
                          <div className="catalog-upload__progress">
                            <div 
                              className="catalog-upload__progress-bar"
                              style={{ width: `${fileUpload.progress}%` }}
                              role="progressbar"
                              aria-valuenow={fileUpload.progress}
                              aria-valuemin={0}
                              aria-valuemax={100}
                              aria-valuetext={`${fileUpload.progress} percent`}
                            />
                          </div>
                        ) : (
                          <span>{fileUpload.progress}%</span>
                        )}
                      </td>
                      <td>
                        <div className="catalog-upload__file-actions">
                          {fileUpload.status === 'waiting' && (
                            <button
                              type="button"
                              onClick={() => startUpload(fileUpload)}
                              className="catalog-upload__action-button"
                              aria-label={`Start upload for ${fileUpload.file.name}`}
                            >
                              <span className="material-symbols-outlined" aria-hidden="true">play_arrow</span>
                            </button>
                          )}
                          
                          <button
                            type="button"
                            onClick={() => removeFile(fileUpload.id)}
                            className="catalog-upload__action-button catalog-upload__action-button--danger"
                            aria-label={`Remove ${fileUpload.file.name}`}
                          >
                            <span className="material-symbols-outlined" aria-hidden="true">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>

        {/* Right panel - validation findings */}
        <aside className="catalog-upload__validation" role="complementary" aria-label="Validation findings">
          <h2>Validation Findings</h2>
          
          <div className="catalog-upload__validation-content">
            {state.files.length === 0 ? (
              <p>Upload files to see validation results.</p>
            ) : (
              <div className="catalog-upload__validation-list">
                {state.files.map((fileUpload) => (
                  <div key={fileUpload.id} className="catalog-upload__validation-item">
                    <h3>{fileUpload.file.name}</h3>
                    
                    {fileUpload.error && (
                      <div className="catalog-upload__validation-error" role="alert">
                        <span className="material-symbols-outlined" aria-hidden="true">error</span>
                        <p>{fileUpload.error}</p>
                      </div>
                    )}
                    
                    {fileUpload.status === 'needs-input' && (
                      <div className="catalog-upload__validation-warning">
                        <span className="material-symbols-outlined" aria-hidden="true">info</span>
                        <p>Coordinate reference system needs to be specified.</p>
                        
                        <div className="catalog-upload__input-group">
                          <label htmlFor={`crs-${fileUpload.id}`}>CRS:</label>
                          <select id={`crs-${fileUpload.id}`}>
                            <option value="">Select CRS</option>
                            <option value="EPSG:4326">WGS 84 (EPSG:4326)</option>
                            <option value="EPSG:3857">Web Mercator (EPSG:3857)</option>
                            <option value="EPSG:32633">UTM Zone 33N (EPSG:32633)</option>
                          </select>
                        </div>
                      </div>
                    )}
                    
                    {fileUpload.status === 'completed' && (
                      <div className="catalog-upload__validation-success" role="status">
                        <span className="material-symbols-outlined" aria-hidden="true">check</span>
                        <p>File uploaded successfully and ready for conversion.</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Bottom actions */}
      <div className="catalog-upload__actions">
        <button
          type="button"
          onClick={() => setState(prev => ({ ...prev, files: [] }))}
          disabled={state.files.length === 0}
        >
          Cancel
        </button>
        
        <button
          type="button"
          disabled={!state.files.some(f => f.status === 'ready')}
        >
          <span className="material-symbols-outlined" aria-hidden="true">fact_check</span>
          Validate
        </button>
        
        <button
          type="button"
          disabled={!state.files.some(f => f.status === 'ready')}
        >
          <span className="material-symbols-outlined" aria-hidden="true">sync_alt</span>
          Convert
        </button>
      </div>
    </div>
  );
}